"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import type { LocaleCode } from "@/lib/i18n/locales";
import { NAV_SECTIONS, sectionSlugs } from "./navMenus";
import { T, useT } from "@/lib/i18n/T";
import { getFacets, searchProducts } from "@/lib/catalog/api";
import { formatMoney } from "@/lib/catalog/format";
import { FacetLabel } from "@/components/catalog/FacetLabel";
import type { FacetValue, ProductCard } from "@/lib/catalog/types";
import "./search.css";

const RECENT_KEY = "rubyn._Q";
const SUGGEST_LIMIT = 6;
/** Shown before the visitor types anything — no backend call needed. */
const TRENDING = ["Lingerie", "Dresses", "Matching Sets", "New In"];

/**
 * Search field with the old dropdown UX: focus opens a panel with section
 * tabs, recent queries and trending; typing surfaces categories + live
 * product hits; Enter goes to the results page. Live product results require
 * the backend search endpoint (see BACKEND_NEEDS.md) — until then the panel
 * shows recent + trending shells.
 */
export function SearchInput({ locale }: { locale: LocaleCode }) {
  const tr = useT();
  const router = useRouter();
  const L = (p: string) => `/${locale}${p}`;
  const boxRef = useRef<HTMLDivElement>(null);

  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [section, setSection] = useState("fashion");
  const [recent, setRecent] = useState<string[]>([]);
  const [hits, setHits] = useState<ProductCard[]>([]);
  const [categories, setCategories] = useState<FacetValue[]>([]);

  // Load recent queries.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(RECENT_KEY);
      if (raw) setRecent(JSON.parse(raw));
    } catch {
      /* ignore */
    }
  }, []);

  // Close on outside click.
  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  // Category names for the "did you mean a section?" suggestions. Fetched once
  // and filtered in memory: the list is ~140 entries, so a request per keypress
  // would be pure waste.
  useEffect(() => {
    let alive = true;
    getFacets({ section })
      .then((f) => alive && setCategories(f.categories))
      .catch(() => alive && setCategories([]));
    return () => {
      alive = false;
    };
  }, [section]);

  // Live product suggestions, debounced. A request in flight is aborted when the
  // next keystroke arrives, so a slow early response can't overwrite a newer one.
  useEffect(() => {
    const q = prompt.trim();
    if (!q) {
      setHits([]);
      return;
    }
    const controller = new AbortController();
    const timer = setTimeout(() => {
      searchProducts(q, { section }, { limit: SUGGEST_LIMIT, signal: controller.signal })
        .then((page) => setHits(page.items))
        .catch(() => {
          /* aborted, or the backend is down — keep the panel usable */
        });
    }, 250);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [prompt, section]);

  /** Category names containing what has been typed so far. */
  const categoryHints = prompt.trim()
    ? categories
        .filter((c) => c.label.toLowerCase().includes(prompt.trim().toLowerCase()))
        .slice(0, 5)
    : [];

  function pushRecent(q: string) {
    try {
      const next = [q, ...recent.filter((x) => x !== q)].slice(0, 10);
      setRecent(next);
      localStorage.setItem(RECENT_KEY, JSON.stringify(next));
    } catch {
      /* ignore */
    }
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      e.preventDefault();
      const q = prompt.trim();
      if (!q) return;
      pushRecent(q);
      setOpen(false);
      router.push(L(`/search?q=${encodeURIComponent(q)}`));
    }
  }

  return (
    <div ref={boxRef} className="rubyn-search">
      <div className={`rubyn-search-input ${open ? "open" : ""}`}>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M7 2.5C4.51472 2.5 2.5 4.51472 2.5 7C2.5 9.48528 4.51472 11.5 7 11.5C8.24278 11.5 9.36709 10.9969 10.182 10.182C10.9969 9.36709 11.5 8.24278 11.5 7C11.5 4.51472 9.48528 2.5 7 2.5ZM1.5 7C1.5 3.96243 3.96243 1.5 7 1.5C10.0376 1.5 12.5 3.96243 12.5 7C12.5 8.33855 12.0213 9.56604 11.2266 10.5195L14.3536 13.6464C14.5488 13.8417 14.5488 14.1583 14.3536 14.3536C14.1583 14.5488 13.8417 14.5488 13.6464 14.3536L10.5195 11.2266C9.56604 12.0213 8.33855 12.5 7 12.5C3.96243 12.5 1.5 10.0376 1.5 7Z"
            fillOpacity="0.6"
          />
        </svg>
        <input
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={tr("search.placeholder")}
          aria-label={tr("search.label")}
        />
      </div>

      {open && (
        <div className="rubyn-search-panel">
          <div className="rubyn-search-tabs">
            {sectionSlugs().map((k) => (
                <button
                  key={k}
                  className={`rubyn-search-tab ${section === k ? "active" : ""}`}
                  onClick={() => setSection(k)}
                >
                  <T id={NAV_SECTIONS[k].titleId} />
                </button>
              ))}
          </div>

          <div className="rubyn-search-body">
            {prompt.trim() ? (
              <>
                <div className="rubyn-search-group">
                  <h4><T id="search.categories" /></h4>
                  <div className="rubyn-search-labels">
                    <Link href={L(`/search?q=${encodeURIComponent(prompt)}`)} className="rubyn-search-label">
                      {prompt}
                    </Link>
                    {categoryHints.map((c) => (
                      <Link
                        key={c.value}
                        href={L(`/${section}/${c.value}`)}
                        className="rubyn-search-label"
                        onClick={() => setOpen(false)}
                      >
                        <FacetLabel section={section} value={c.value} label={c.label} />
                      </Link>
                    ))}
                  </div>
                </div>
                {hits.length > 0 && (
                  <div className="rubyn-search-group">
                    <h4><T id="search.products" /></h4>
                    <div className="rubyn-search-products">
                      {hits.map((h) => (
                        <Link
                          key={h.id}
                          href={L(`/${h.section}/products/${h.slug}`)}
                          className="rubyn-search-product"
                          onClick={() => setOpen(false)}
                        >
                          <span
                            className="rubyn-search-product-img"
                            style={{ backgroundImage: h.image ? `url(${h.image})` : undefined }}
                          />
                          <span className="rubyn-search-product-text">
                            <h5>{h.title}</h5>
                            {h.price && <h6>{formatMoney(locale, h.price)}</h6>}
                          </span>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                {recent.length > 0 && (
                  <div className="rubyn-search-group">
                    <h4><T id="search.recent" /></h4>
                    <div className="rubyn-search-labels">
                      {recent.map((q) => (
                        <Link key={q} href={L(`/search?q=${encodeURIComponent(q)}`)} className="rubyn-search-label">
                          {q}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
                <div className="rubyn-search-group">
                  <h4><T id="search.trending" /></h4>
                  <div className="rubyn-search-labels">
                    {TRENDING.map((t) => (
                      <Link key={t} href={L(`/search?q=${encodeURIComponent(t)}`)} className="rubyn-search-label">
                        {t}
                      </Link>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
